import {
  debounce,
  type DebounceOptions,
  type DebouncedFunction,
} from "es-toolkit";
import { useMemo, useEffect } from "react";

/** Avoid user to click several times */
export const DEBOUNCE_DELAY_MS = 500;

/**
 * Debounced callback for functions that accept a single argument of type T.
 * Forwards all debounce options to es-toolkit debounce.
 * Returns the debounced function.
 */
export function useDebouncedCallback<T>(
  callback: (value: T) => void,
  delay: number,
  options?: DebounceOptions
): DebouncedFunction<(value: T) => void> {
  const debouncedFn = useMemo(() => {
    function wrapped(value: T): void {
      callback(value);
    }

    return debounce(wrapped, delay, options);
  }, [callback, delay, options]);

  useEffect(() => {
    return () => {
      debouncedFn.cancel();
    };
  }, [debouncedFn]);

  return debouncedFn;
}
